import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDateTime } from '../api';
import { ROLE_LABEL, useAuth } from '../auth';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';
import type { ManagedUser, Role } from '../types';

const ROLES: Role[] = [
  'admin',
  'kaufmann',
  'pruefer',
  'disponent',
  'schichtleiter',
  'lagerist',
  'auftraggeber',
];

const ROLE_HINT: Record<Role, string> = {
  admin: 'Vollzugriff inklusive Benutzerverwaltung',
  kaufmann: 'Stammdaten und kaufmännische Vorgänge, keine Lagerbuchungen',
  pruefer:
    'Betriebsprüfung nach § 147 Abs. 6 AO: sieht alles Steuerrelevante, kann nichts ändern. Nach der Prüfung deaktivieren.',
  disponent: 'Stammdaten, Lagerbuchungen und Inventurabschluss',
  schichtleiter: 'Lagerbuchungen und Inventurabschluss',
  lagerist: 'Lagerbuchungen und Zählung',
  auftraggeber: 'Nur Lesezugriff auf die eigene Ware',
};

interface FormState {
  id: string | null;
  email: string;
  username: string;
  name: string;
  password: string;
  role: Role;
  ownerId: string;
  active: boolean;
  istKassierer: boolean;
}

const emptyForm: FormState = {
  id: null,
  email: '',
  username: '',
  name: '',
  password: '',
  role: 'lagerist',
  ownerId: '',
  active: true,
  istKassierer: false,
};

export function UsersPage() {
  const { owners } = useAuth();
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  // Eigene (vom Betrieb angelegte) Rollen - zusätzlich zu den eingebauten.
  const [eigeneRollen, setEigeneRollen] = useState<{ role: string; label: string; basis: string }[]>([]);

  const load = useCallback(async () => {
    try {
      setUsers(await api<ManagedUser[]>('/api/users'));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
    api<{ role: string; label: string; basis: string }[]>('/api/rollen/eigene')
      .then(setEigeneRollen)
      .catch(() => setEigeneRollen([]));
  }, [load]);

  const rolleLabel = (role: string) =>
    ROLE_LABEL[role as Role] ?? eigeneRollen.find((r) => r.role === role)?.label ?? role;

  async function save() {
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      if (form.id) {
        await api(`/api/users/${form.id}`, {
          method: 'PUT',
          body: {
            name: form.name,
            username: form.username.trim() || null,
            role: form.role,
            ownerId: form.ownerId || null,
            active: form.active,
            istKassierer: form.istKassierer,
            ...(form.password ? { password: form.password } : {}),
          },
        });
      } else {
        await api('/api/users', {
          method: 'POST',
          body: {
            email: form.email,
            username: form.username.trim() || null,
            name: form.name,
            password: form.password,
            role: form.role,
            ownerId: form.ownerId || null,
            istKassierer: form.istKassierer,
          },
        });
      }
      setMessage(`Benutzer ${form.name} gespeichert.`);
      setForm(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {form && (
        <Card title={form.id ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}>
          <div className="form-grid">
            {!form.id && (
              <TextField
                label="E-Mail"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
              />
            )}
            <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <TextField
              label="Benutzername (optional)"
              value={form.username}
              onChange={(v) => setForm({ ...form, username: v })}
              hint="Anmeldung wahlweise mit Benutzername oder E-Mail"
            />
            <TextField
              label={form.id ? 'Neues Passwort (optional)' : 'Passwort'}
              type="password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              hint="mindestens 10 Zeichen"
            />
            <label className="field">
              <span>Rolle</span>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABEL[role]}
                  </option>
                ))}
                {eigeneRollen.length > 0 && (
                  <optgroup label="Eigene Rollen">
                    {eigeneRollen.map((r) => (
                      <option key={r.role} value={r.role}>
                        {r.label}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <span className="small muted">
                {ROLE_HINT[form.role] ??
                  `Eigene Rolle (Vorlage: ${rolleLabel(
                    eigeneRollen.find((r) => r.role === form.role)?.basis ?? '',
                  )}) – Rechte unter „Rollen & Rechte".`}
              </span>
            </label>
            {form.role === 'auftraggeber' && (
              <label className="field">
                <span>Zugeordneter Auftraggeber</span>
                <select
                  value={form.ownerId}
                  onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
                >
                  <option value="">— bitte wählen —</option>
                  {owners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {form.role !== 'auftraggeber' && (
            <div style={{ marginTop: 4 }}>
              <CheckField
                label="Auch Kassierer"
                checked={form.istKassierer}
                onChange={(v) => setForm({ ...form, istKassierer: v })}
              />
              <span className="small muted">
                Legt für diesen Benutzer einen Kassierer-Eintrag an, damit er im Kassen-Popup zur
                Auswahl steht — ohne doppelte Pflege. Ohne Haken kassiert er nicht.
              </span>
            </div>
          )}

          {form.id && (
            <CheckField label="Aktiv" checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" onClick={save} disabled={busy}>
              {busy ? 'Speichern …' : 'Speichern'}
            </button>
            <button onClick={() => setForm(null)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <div className="toolbar">
        <span className="spacer" />
        <button className="primary" onClick={() => setForm({ ...emptyForm })}>
          Neuer Benutzer
        </button>
      </div>

      <Card padded={false}>
        {!users ? (
          <Loading />
        ) : users.length === 0 ? (
          <Empty>Keine Benutzer.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Auftraggeber</th>
                <th>Letzte Anmeldung</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td className="small">
                    {user.email}
                    {user.username && <div className="muted">Benutzer: {user.username}</div>}
                  </td>
                  <td>
                    {rolleLabel(user.role)}
                    {user.ist_kassierer && (
                      <>
                        {' '}
                        <Badge kind="info">Kassierer</Badge>
                      </>
                    )}
                  </td>
                  <td className="small">{user.owner_name ?? '—'}</td>
                  <td className="small muted">
                    {user.last_login_at ? formatDateTime(user.last_login_at) : 'nie'}
                  </td>
                  <td>{user.active ? <Badge kind="ok">aktiv</Badge> : <Badge kind="bad">gesperrt</Badge>}</td>
                  <td className="num">
                    <button
                      className="small ghost"
                      onClick={() =>
                        setForm({
                          id: user.id,
                          email: user.email,
                          username: user.username ?? '',
                          name: user.name,
                          password: '',
                          role: user.role,
                          ownerId: user.owner_id ?? '',
                          active: user.active,
                          istKassierer: user.ist_kassierer,
                        })
                      }
                    >
                      bearbeiten
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
